import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { SliceQuery, SliceUpdateQuery, SliceInsertQuery } from '../api/api.defs';
import { ClientRow, CustomerRow } from './clients.defs';
import { environment } from 'src/environments/environment';
import { map, tap } from 'rxjs/operators';
import { Client } from './client';
import { SettingsService } from '../settings/settings.service';
import { SettingsRow } from '../settings/settings.defs';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ClientsService {

	index = new Map<number, Client>();
	private _phoneIndex = new Map<string, Client[]>();

	constructor(
		private _api: ApiService,
		private _settings: SettingsService,
	) { }

	getByPhone(tel: string): Client[] {
		return this._phoneIndex.get(tel) || [];
	}

	disassociate(client: Client) {
		return this._api
			.request<SliceUpdateQuery>({'$/slice/xupdate': {
					slice: environment.slices.clients,
					rows: [{
						id: client.id,
						NUMBERRef: this._settings.active.noNumberRef,
					}]
				}}
			)
			.pipe(
				tap(resp => {
					const cache = this._phoneIndex.get(client.number.NUMBER);
					if (cache) {
						// try and remove it..
						const idx = cache.findIndex(c => c.id === client.id)
						if (idx >= 0) {
							cache.splice(idx, 1);
						}
					}
					console.log('responded', resp);
				})
			);
	}

	rename(client: Client, name: string) {
		return this._api
			.request<SliceUpdateQuery>({'$/slice/xupdate': {
				slice: environment.slices.clients,
				rows: [{
					id: client.id,
					Name: name,
				}]
			}})
			.pipe(
				tap(resp => {
					if (resp && resp.keys && resp.keys[0]) {
						client.name = name; // did we alter?
					}
				})
			)
	}

	addName(name: string, number: string) {
		// we need to make a check to see if this number exists
		return new Observable<Client>(obs => {
			this._api.request<any>({'$/slice/report': {
				slice: environment.slices.customers,
				where: {NUMBER: number},
			}})
			.subscribe(resp => {
				const req: any[] = [];
				if (resp && resp.rows) {
					const customer = resp.rows[0];
					if (!customer) {
						req.push(
							'customerInsert', {'!/slice/xinsert': {
								slice: environment.slices.customers,
								rows: [{NUMBER: number}],
							}},
							'client', {'!/slice/xinsert': {
								slice: environment.slices.clients,
								rows: [{Name: name, NUMBERRef: {'$_': 'customerInsert.keys.0'}}],
							}},
							'customer', {'!/slice/report': {
								slice: environment.slices.customers,
								where: {id: {$_: 'customerInsert.keys.0'}},
							}}
						)
					} else {
						req.push('client', {'!/slice/xinsert': {
							slice: environment.slices.clients,
							rows: [{Name: name, NUMBERRef: customer.id}],
						}},
						'customer', {'!/slice/report': {
							slice: environment.slices.customers,
							where: {id: customer.id}
						}},
						)
					}
					req.push('report', {'!/slice/report': {
						slice: environment.slices.clients,
						where: {id: {$_: 'client.keys.0'}},
					}},
						'all', {$_: '*'}
					)
				}
				this._api
					.request<{report: SliceQuery<ClientRow>, customer: SliceQuery<CustomerRow>}>({'$/tools/do': req})
					.subscribe(resp => {

						const client = resp.report,
							customer = resp.customer;

						if (client && client.rows && client.rows[0] && customer && customer.rows && customer.rows[0]) {
							const clientRow = client.rows[0],
								customerRow = customer.rows[0],
								c = new Client(clientRow, customerRow);

							if (!this._phoneIndex.has(customerRow.NUMBER)) {
								this._phoneIndex.set(customerRow.NUMBER, [c])
							} else {
								this._phoneIndex.get(customerRow.NUMBER).push(c);
							}
							obs.next(c);
						} else {
							obs.error('insert_failure');
						}
					})
			})
		})
	}

	fetch() {
        return this._api
        .request<{
            clients: SliceQuery<ClientRow>,
            numbers: SliceQuery<CustomerRow>,
            settings: SliceQuery<SettingsRow>
        }>({
            clients: {'$/slice/report': {
                slice: environment.slices.clients,
                fields: {
                    Name: 'Name',
                    NUMBERRef: 'NUMBERRef',
                    remove_0: 'remove_0',
                    id: 'id',
                    Barber: 'Barber',
                },
                order: [['$field', 'Name']],
            }},
            numbers: {'$/slice/report': {
                slice: environment.slices.customers,
                fields: {
                    ignoreInvoicePooling_0: 'ignoreInvoicePooling_0',
                    NUMBER: 'NUMBER',
                    id: 'id',
                }
            }},
            settings: this._settings.getRequest(new Date()),
        }, 'clients.fetch')
        .pipe(
            map(resp => {
                const clients = resp.clients.rows,
                    numbers = resp.numbers.rows,
                    numberIndex = new Map<number, CustomerRow>(),
                    index = this.index,
                    sett = this._settings.active || (this._settings.active = resp.settings.rows.shift()),
                    badRef = sett.noNumberRef;

                index.clear();

                // seed our index, to make pulls easier
                numbers.forEach(number => {
                    numberIndex.set(number.id, number)
                    this._phoneIndex.set(number.NUMBER, []);
                })

                clients.forEach(client => {
                    const cust = numberIndex.get(client.NUMBERRef) || numberIndex.get(sett.noNumberRef),
                        c = new Client(client, cust);
                    index.set(client.id, c);
                    if (c.customerId !== badRef) {
                        this._phoneIndex.get(cust.NUMBER).push(c);
                    }
                });

                return index;
            }),
        );
	}
}
